export const ARTICLES = [
  {
    slug: "cabin-crew-assessment-day-insider-guide",
    title: "What Actually Happens at a Cabin Crew Assessment Day (From Someone Who Ran Them)",
    excerpt: "I sat on the airline hiring team. Here's what really goes on behind the closed doors of an assessment day — and what the assessors are writing down while you talk.",
    category: "Assessment Day",
    date: "9 September 2026",
    readTime: "6 min read",
    content: [
      {
        h: "First, a secret: the assessment starts in the car park",
        paragraphs: [
          "Most candidates think the assessment day begins when they sit down in the group exercise. It doesn't. It begins the moment you arrive. Assessors and recruiters notice who arrives flustered and late, who is rude to the receptionist, and who is sitting in the corner scrolling their phone while everyone else introduces themselves.",
          "I know this because I was one of those assessors. Before the first exercise even began, we often had a quiet note or two already. The good news: this is the easiest part of the day to get right. Arrive early, be warm to everyone you meet — staff and candidates alike — and treat every person in the building as if they're on the panel. Sometimes they are.",
        ],
      },
      {
        h: "The reach test and height check — don't stress",
        paragraphs: [
          "Early in the day there's usually a reach test (commonly around 212cm barefoot) and sometimes a height check. This is purely a safety requirement — you need to be able to reach overhead emergency equipment. Nobody is judging you here. If you're close to the mark, practice your reach at home: stand flat, stretch through your fingertips, and learn to extend without jumping. Jumping is usually a fail; a full, controlled stretch is not.",
        ],
      },
      {
        h: "The group exercise — what we're actually scoring",
        paragraphs: [
          "This is where most offers are won and lost. You'll be put in a group of 6–10 candidates and given a scenario — often something like prioritising items after a crash landing, or planning an event with limited resources. Here's what candidates think we're scoring: who has the best ideas. Here's what we're actually scoring:",
        ],
        list: [
          "Do you listen, or just wait for your turn to speak?",
          "Do you draw quieter people in? ('Sarah, what do you think?')",
          "Can you disagree without being disagreeable?",
          "Do you notice the time and the task, not just the discussion?",
          "Do you build on other people's ideas rather than competing with them?",
        ],
        paragraphs_after: [
          "The candidates who get hired are rarely the loudest. They're the ones who made the group better. I've watched candidates talk themselves out of a job in five minutes, and I've watched others secure one with three well-chosen sentences.",
        ],
      },
      {
        h: "The behavioural interview — stories beat adjectives",
        paragraphs: [
          "You'll face questions like 'Tell me about a time you dealt with a difficult customer.' Every assessor has heard a thousand answers that start with 'I'm a real people person.' That tells us nothing. What works is a specific, true story with a clear result — the STAR method (Situation, Task, Action, Result).",
          "Prepare five or six stories before the day: a difficult customer, a team under pressure, a mistake you owned, a time you followed a rule you disagreed with, a time you went above and beyond. If you've never worked in customer service, draw from sport, volunteering, family businesses — we care about the behaviour, not the industry.",
        ],
      },
      {
        h: "Grooming — yes, it matters, and here's the standard",
        paragraphs: [
          "Cabin crew are the face of the airline, and assessment day is a uniform audition. Business attire, polished closed-toe shoes, conservative hair, natural makeup, short neutral nails, minimal jewellery. Tattoos and piercings should follow the airline's uniform policy — check it before the day. You don't need to be glamorous; you need to look like the airline could put you in a uniform tomorrow.",
        ],
      },
      {
        h: "The one thing that separates offers from rejections",
        paragraphs: [
          "After hundreds of assessments, the pattern is unmistakable: the people who get offers are the ones who made it easy to imagine them on board. Calm under pressure, warm with strangers, coachable, and genuine. You can't fake that in a single answer — but you can absolutely prepare for it.",
          "That's exactly what I work on in my Interview Ready sessions: we run a real mock assessment, I score you the way the panel would, and you leave knowing precisely what to fix. If your assessment day is coming up, come and sit the hard version with me first.",
        ],
      },
    ],
  },
  {
    slug: "group-exercise-what-assessors-score",
    title: "The Group Exercise: What Assessors Are Really Scoring",
    excerpt: "It's the most feared part of the assessment day — and the most misunderstood. Here's what the people with the clipboards are actually writing down, from someone who held the clipboard.",
    category: "Assessment Day",
    date: "10 September 2026",
    readTime: "5 min read",
    content: [
      {
        h: "Why airlines bother with a group exercise at all",
        paragraphs: [
          "Cabin crew never work alone. Every flight is a team of people who may have met twenty minutes before sign-on, and they need to function perfectly together at 38,000 feet — including in an emergency. The group exercise exists to answer one question: what is this person like to work with?",
          "That's it. That's the whole exercise. Everything else — the scenario, the task, the time limit — is just scaffolding to make that question visible.",
        ],
      },
      {
        h: "The scenario itself barely matters",
        paragraphs: [
          "You'll usually get something like: 'Your plane has crashed in the desert. Rank these 12 items in order of importance.' Candidates spend enormous energy debating whether the torch outranks the mirror. Here's the truth from the other side of the table: we do not care about your ranking. There is no perfect answer, and nobody's offer has ever hinged on choosing the compass.",
          "What we are doing while you debate is filling in a scoresheet. And that scoresheet has nothing to do with survival equipment.",
        ],
      },
      {
        h: "What's actually on the scoresheet",
        paragraphs: ["Every airline words it differently, but the criteria are remarkably consistent:"],
        list: [
          "Communication — do you speak clearly and make your point in thirty seconds rather than three minutes?",
          "Listening — do you respond to what others actually said, or just wait for your turn?",
          "Inclusion — do you notice the quiet person and bring them in? ('Priya, you've been thinking — what do you reckon?')",
          "Constructiveness — do you build on ideas ('Yes, and...') rather than compete with them?",
          "Task and time awareness — does anyone keep the group moving toward a decision? Volunteering as timekeeper is free points.",
          "Grace under disagreement — can you be outvoted and support the group's direction anyway? That is literally the job.",
        ],
      },
      {
        h: "The three ways candidates fail",
        paragraphs: [
          "The dominator: talks the most, mistakes volume for leadership, interrupts. We see it every assessment day, and it scores poorly every time.",
          "The ghost: says almost nothing. We understand nerves — but if we can't see you, we can't score you. Aim to contribute meaningfully at least three or four times. Early is easier than late; the longer you wait, the harder it gets.",
          "The critic: wins points by shooting down other candidates' ideas. Panels are unanimous on this one — nobody wants that person on their crew.",
        ],
      },
      {
        h: "What a hire looks like",
        paragraphs: [
          "The candidate who gets the offer usually says less than you'd expect. They make two or three clear points. They credit others ('Building on what Sam said...'). They include someone quiet. They notice the time. And when the group goes a different way, they get behind it without sulking.",
          "None of that requires you to be the smartest person in the room. It requires you to make the room better.",
        ],
      },
      {
        h: "How to practice this before the day",
        paragraphs: [
          "Group behaviour is a skill, and skills improve with reps. In my Interview Ready sessions we run a realistic mock exercise — I play assessor, score you against the real criteria, and show you exactly where your habits help or hurt you. Candidates consistently tell me the real day feels easier than my practice run. That's the idea.",
        ],
      },
    ],
  },
];

export function getArticle(slug) {
  return ARTICLES.find((a) => a.slug === slug);
}
